import {
	getOnboardingDestination,
	getOnboardingFloor,
	isResumableStep,
	OnboardingStatus,
	WIZARD_STEP,
} from '@/entities/onboarding-status';

export const ONBOARDING_STEP_CACHE_KEY = 'onboarding-step';

const ONBOARDING_STORAGE_KEYS = [
	'onboarding-ulid',
	ONBOARDING_STEP_CACHE_KEY,
	'onboarding-payment-method',
	'checkout-id',
	'checkout-url',
];

/** Forgets this browser's copy of a process, e.g. once it is abandoned. */
export function clearOnboardingStorage() {
	ONBOARDING_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
}

/** How the wizard opens once the backend status is known. */
export type OnboardingEntry =
	| { kind: 'start'; step: number }
	| { kind: 'resume'; step: number; floor: number }
	// A certificate holder with a process in flight picks resume or start new.
	| { kind: 'choose'; step: number; floor: number }
	// The request waits on the Trust center; starting another is not offered.
	| { kind: 'in_review' }
	| { kind: 'dashboard' };

/**
 * The cached wizard step only covers what the backend cannot tell apart: the
 * start screen, the agreement and the form, all of which are FORM to it.
 */
export function parseCachedStep(value: string | null): number {
	const parsed = value === null ? NaN : parseInt(value, 10);
	if (isNaN(parsed)) {
		return WIZARD_STEP.START;
	}
	return Math.min(Math.max(parsed, WIZARD_STEP.START), WIZARD_STEP.FORM);
}

export function decideOnboardingEntry({
	status,
	cachedStep,
	isNewSignatureRequest,
}: {
	status: OnboardingStatus | null;
	cachedStep: number;
	isNewSignatureRequest: boolean;
}): OnboardingEntry {
	// ?signature=new without a process in flight keeps the new-certificate
	// flow from the start.
	const start: OnboardingEntry = {
		kind: 'start',
		step: isNewSignatureRequest ? WIZARD_STEP.START : cachedStep,
	};

	if (!status) {
		return start;
	}

	if (isResumableStep(status.step)) {
		const destination = getOnboardingDestination(status.step);
		if (destination.kind !== 'wizard') {
			return start;
		}
		return {
			kind: status.has_active_certificate ? 'choose' : 'resume',
			step: destination.step,
			floor: getOnboardingFloor(status.step),
		};
	}

	const destination = getOnboardingDestination(status.step);

	// On ?signature=new too, so a request in review is not replaced by another.
	if (destination.kind === 'in_review') {
		return { kind: 'in_review' };
	}

	if (!isNewSignatureRequest && destination.kind === 'dashboard') {
		return { kind: 'dashboard' };
	}

	return start;
}

/**
 * Raises the back-navigation floor after a status refresh. The floor never
 * moves above the step the user is on, so a refresh that lands before the
 * wizard catches up cannot strand the user below it.
 */
export function raiseFloor(
	currentFloor: number,
	currentStep: number,
	backendStep: string,
): number {
	const floor = getOnboardingFloor(backendStep);
	return currentStep >= floor ? Math.max(currentFloor, floor) : currentFloor;
}

/** Backward navigation stays between the floor and the current step. */
export function clampBackStep(
	target: number,
	floor: number,
	currentStep: number,
): number {
	return Math.min(Math.max(target, floor), currentStep);
}
